/**
 * Landing page: the caller's games, and a button to start one.
 *
 * Backed by a real D1 query over `game_membership`, rather than the original's
 * `gamesIndex` array on the user document -- which was capped at 20 entries,
 * rewritten in full on every change, and required one Firestore read per game
 * just to render the list.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { GameSummary } from '../lib/api';
import { InstallPrompt } from './InstallPrompt';
import { NewGameWizard } from './NewGameWizard';
import { formatKickoffDay } from '../lib/format';
import { groupBySeason } from '../../shared/season';
import { sportOf } from '../../shared/sports';
import type { SessionUser } from './App';
import { APP_NAME, APP_TAGLINE, UNTITLED_GAME } from '../../shared/branding';
import { OwnerTools } from './OwnerTools';

interface Props {
  user: SessionUser;
  onOpenGame: (id: string) => void;
}

/**
 * The first three filter by role among active games; 'archived' switches to
 * the shelf instead, across all roles.
 */
type Filter = 'all' | 'admin' | 'player' | 'archived';

export function GamesLanding({ onOpenGame }: Props) {
  const [games, setGames] = useState<GameSummary[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  /** Id of the game showing its inline "really delete?" prompt. */
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [busyArchiving, setBusyArchiving] = useState<string | null>(null);

  const showingArchive = filter === 'archived';

  const load = useCallback((archived: boolean) => {
    setLoading(true);
    api
      .listGames(archived)
      .then((res) => {
        setGames(res.games);
        setArchivedCount(res.archivedCount);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Refetches when crossing between the active list and the archive: they are
  // different queries, not a client-side filter.
  useEffect(() => {
    load(showingArchive);
  }, [load, showingArchive]);

  /** The "set it up by hand" escape hatch out of the wizard. */
  const createBlankGame = async () => {
    setCreating(true);
    setError(null);
    try {
      const game = await api.createGame({ title: UNTITLED_GAME });
      onOpenGame(game.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the game.');
      setCreating(false);
    }
  };

  const deleteGame = async (game: GameSummary) => {
    setDeleting(game.id);
    setError(null);
    try {
      await api.deleteGame(game.id);
      // Drop it locally rather than refetching: the list is already correct
      // everywhere else, and a refetch would flash the whole grid.
      setGames((current) => current.filter((g) => g.id !== game.id));
      setConfirmingDelete(null);
      if (game.archived_at) setArchivedCount((n) => Math.max(0, n - 1));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that game.');
    } finally {
      setDeleting(null);
    }
  };

  const setArchived = async (game: GameSummary, archived: boolean) => {
    setBusyArchiving(game.id);
    setError(null);
    try {
      await api.setArchived(game.id, archived);
      // Either way the game leaves whichever list is currently on screen.
      setGames((current) => current.filter((g) => g.id !== game.id));
      setArchivedCount((n) => Math.max(0, archived ? n + 1 : n - 1));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update that game.');
    } finally {
      setBusyArchiving(null);
    }
  };

  const visible = showingArchive
    ? games
    : games.filter((g) => filter === 'all' || g.role === filter);

  /*
   * The empty state offers its own way into the archive, but only when there
   * is nothing active to show. Two controls doing the same thing is clutter;
   * dropping the header one outright would strand anyone whose list is not
   * empty, since the centre one never renders for them.
   */
  const emptyStateOffersArchive =
    !loading && !showingArchive && games.length === 0 && archivedCount > 0;
  const showHeaderArchiveLink =
    showingArchive || (archivedCount > 0 && !emptyStateOffersArchive);

  /**
   * One card. Extracted because the list is rendered once per season when
   * more than one is in play, and the card is identical in each.
   */
  const renderCard = (game: GameSummary) => (
          <div className="tracker-card game-card" key={game.id}>
            {/* The card body is the button, not the card itself: a delete
                control nested inside a button is invalid markup and unreachable
                by keyboard. */}
            <button
              type="button"
              className="game-card-open"
              onClick={() => onOpenGame(game.id)}
            >
              <span className="tc-name">{game.title}</span>
              <span className="tc-squares">
                {game.home_team} vs {game.away_team}
              </span>
              {/* Kickoff comes from the fixture; it is the thing people scan for. */}
              {formatKickoffDay(game.kickoff_at) && (
                <span className="tc-squares">{formatKickoffDay(game.kickoff_at)}</span>
              )}
              <span className="row" style={{ marginTop: 8 }}>
                <span className={`badge ${game.role === 'admin' ? 'admin' : ''}`}>
                  {game.role}
                </span>
                <span className="badge">{game.status}</span>
              </span>
            </button>

            {game.role === 'admin' &&
              (confirmingDelete === game.id ? (
                <div className="game-card-confirm" role="group" aria-label="Confirm delete">
                  <span className="small">
                    Delete permanently? The board and everyone&rsquo;s squares go
                    with it.
                  </span>
                  <div className="row" style={{ gap: 8, marginTop: 8 }}>
                    <button
                      type="button"
                      className="btn-danger"
                      disabled={deleting === game.id}
                      onClick={() => void deleteGame(game)}
                    >
                      {deleting === game.id ? 'Deleting…' : 'Delete'}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setConfirmingDelete(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="game-card-actions">
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={busyArchiving === game.id}
                    aria-label={
                      showingArchive
                        ? `Put ${game.title} back on the main list`
                        : `Archive ${game.title}`
                    }
                    title={showingArchive ? 'Put back on the main list' : 'Archive'}
                    onClick={() => void setArchived(game, !showingArchive)}
                  >
                    {showingArchive ? 'Restore' : 'Archive'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    aria-label={`Delete ${game.title}`}
                    title="Delete this game"
                    onClick={() => setConfirmingDelete(game.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
  );

  /**
   * Games bucketed by season, newest first.
   *
   * Squares pools are seasonal: a list that runs straight from this week's
   * game into one from two Novembers ago reads as clutter rather than history.
   * Grouping keeps last year's archive legible without hiding it.
   */
  const seasonGroups = groupBySeason(
    visible,
    (game) => ({ year: game.season ?? null, sport: sportOf(game.league) }),
    (game) => Date.parse(game.kickoff_at ?? game.updated_at) || 0,
  );

  return (
    <>
      <header className="banner">
        <h1>{APP_NAME}</h1>
        <div className="banner-meta">{APP_TAGLINE}</div>
      </header>

      <InstallPrompt />

      {/* The action comes first and on its own: it is not one of the filters. */}
      <button
        type="button"
        className="btn-primary new-game"
        onClick={() => setWizardOpen(true)}
        disabled={creating}
      >
        {creating ? 'Creating\u2026' : '+  New game'}
      </button>

      <div className="row list-head">
        <div style={{ minWidth: 0 }}>
          <h2 className="card-title" style={{ margin: 0 }}>
            {showingArchive ? 'Archived' : 'Your games'}
          </h2>
          <p className="small muted" style={{ marginTop: 2 }}>
            {showingArchive
              ? 'Put away, but not deleted. Restore any of them.'
              : 'Pools you run, and pools you have squares in.'}
          </p>
        </div>
        <div className="spacer" />
        {/* Archived is a different view, not a fourth role filter, so it sits
            apart from the segmented control below. */}
        {showHeaderArchiveLink && (
          <button
            type="button"
            className="btn-ghost small archive-link"
            onClick={() => setFilter(showingArchive ? 'all' : 'archived')}
          >
            {showingArchive
              ? '\u2190 Back to your games'
              : `Archived (${archivedCount})`}
          </button>
        )}
      </div>

      {/* Only worth showing when there is actually something to narrow down. */}
      {!showingArchive && games.length > 0 && (
        <div className="segmented" role="tablist" aria-label="Filter games">
          {(
            [
              ['all', 'All', games.length],
              ['admin', 'Hosting', games.filter((g) => g.role === 'admin').length],
              ['player', 'Playing', games.filter((g) => g.role === 'player').length],
            ] as [Filter, string, number][]
          ).map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={filter === value ? 'is-selected' : ''}
              onClick={() => setFilter(value)}
            >
              {label}
              <span className="segmented-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="small" role="alert" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}
      {loading && <p className="empty-state">Loading…</p>}

      {!loading && visible.length === 0 && (
        <div className="empty-state">
          {showingArchive ? (
            <p>Nothing archived.</p>
          ) : games.length > 0 ? (
            // The active list has games, just none matching this role filter.
            <p>
              No games you&rsquo;re {filter === 'admin' ? 'running' : 'playing in'}.
            </p>
          ) : archivedCount > 0 ? (
            // Not "no games yet" -- they have some, just none active. Saying
            // otherwise sends people to create a duplicate.
            <>
              <p>No active games.</p>
              <p className="small">
                {archivedCount} {archivedCount === 1 ? 'game is' : 'games are'} archived.
              </p>
              <button
                type="button"
                className="btn-ghost"
                style={{ marginTop: 12 }}
                onClick={() => setFilter('archived')}
              >
                View archived
              </button>
            </>
          ) : (
            <>
              <p>No games yet.</p>
              <p className="small">
                Create one, or open an invite link someone sent you.
              </p>
            </>
          )}
        </div>
      )}

      {seasonGroups.map(({ label, items }) => (
        <section key={label}>
          {/* Only labelled once a second season exists. Within a single season
              the heading is noise: every game is in it. */}
          {seasonGroups.length > 1 && <h3 className="season-heading">{label}</h3>}
          <div className="tracker-grid">{items.map(renderCard)}</div>
        </section>
      ))}

      <OwnerTools />

      {wizardOpen && (
        <NewGameWizard
          onCreated={(id) => {
            setWizardOpen(false);
            onOpenGame(id);
          }}
          onManual={() => {
            setWizardOpen(false);
            void createBlankGame();
          }}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </>
  );
}
